import { datadogRum } from '@datadog/browser-rum'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  clearOrderDetail,
  refreshOrderDetail as refreshOrderDetailAction,
} from '../../redux/slices/orders'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useGetOrders from './useGetOrders'
import getGoToChannelUrl, {
  isSupportedChannel,
} from '../../utils/common/getGoToChannelUrl'
import { generateDataDogContext } from '../../utils/dataDog'
import { WorkflowChannel } from '../../graphQL/queries/getSession/types'

/**
 * Query param carrying the selected status tab.
 *
 * Reading it lets a deep link — a bot message pointing at delivered orders,
 * say — open the screen with that tab already selected. Writing it back on
 * every tab change keeps a page reload and the browser back button honest.
 * @internal
 */
export const ORDERS_STATUS_PARAM = 'status'

/**
 * Shape the config DSL emits for an action whose component called back with a
 * bare scalar. `processActions` wraps such an argument as `{ value }`, so a
 * storefront authors `{{this.value}}`.
 * @internal
 */
interface ScalarActionArgs {
  value?: unknown
}

interface OpenOrderDetailArgs extends ScalarActionArgs {
  orderId?: unknown
}

const readString = (...candidates: unknown[]): string => {
  const found = candidates.find(
    (candidate) => typeof candidate === 'string' && candidate.length > 0,
  )
  return typeof found === 'string' ? found : ''
}

/**
 * The EventManager actions the Order History and Order Detail screens
 * dispatch.
 *
 * Rendering lives entirely in SCS config, so these are the only seams between
 * the authored component tree and the orders data layer.
 */
export const useOrderActions = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const slug = useAppSelector((state) => state.defaultSlice.storeName)
  const sessionId = useAppSelector((state) => state.defaultSlice.sessionId)
  const activeFilter = useAppSelector((state) => state.ordersSlice.activeFilter)
  const workflow = useAppSelector(
    (state) => state.defaultSlice.sessionData?.workflow,
  )
  const configPhoneNumber = useAppSelector(
    (state) => state.defaultSlice.config?.phoneNumber,
  )
  const { loadMoreOrders, changeOrdersFilter } = useGetOrders()

  /**
   * Row tap. Accepts either `{{this.value}}` (OrderList calls back with the
   * bare id) or an explicit `orderId` arg, so a storefront can author either.
   */
  const openOrderDetail = (args: OpenOrderDetailArgs = {}) => {
    const orderId = readString(args.orderId, args.value)
    // A row with no id cannot be routed to. Navigating to `/orders/` would
    // fall through the wildcard to NotFound, which reads as a crash.
    if (!orderId || !slug || !sessionId) return

    navigate(`/${slug}/${sessionId}/orders/${orderId}`)
  }

  /**
   * Load-more sentinel. The re-entrancy and has-next-page guards live in
   * `useGetOrders`, so this stays a pass-through.
   */
  const loadMore = () => loadMoreOrders()

  /**
   * Status tab. An empty string is the "All" tab, which drops the param
   * rather than writing `?status=`.
   */
  const filterOrders = (args: ScalarActionArgs = {}) => {
    const status = readString(args.value)

    const nextParams = new URLSearchParams(searchParams)
    if (status) {
      nextParams.set(ORDERS_STATUS_PARAM, status)
    } else {
      nextParams.delete(ORDERS_STATUS_PARAM)
    }
    // `replace` so a run of tab taps leaves one history entry, not one per
    // tap for the back button to walk through.
    setSearchParams(nextParams, { replace: true })

    return changeOrdersFilter(status)
  }

  /**
   * Back from Order Detail to Order History.
   *
   * The status tab is rebuilt from `activeFilter` rather than from the current
   * URL: the detail route carries no `?status`, so navigating to a bare
   * `/orders` would remount History, which reads the param once on mount and
   * would refetch unfiltered — silently dropping the tab the shopper chose.
   */
  const backToHistory = () => {
    if (!slug || !sessionId) return

    const params = new URLSearchParams()
    if (activeFilter) params.set(ORDERS_STATUS_PARAM, activeFilter)
    const query = params.toString()

    // Navigate first, reset second. The reset blanks `selected`, and on an
    // unbatched render — the host shell is on react-dom 17, and these handlers
    // arrive from an EventEmitter, outside React's synthetic event system —
    // reversing the order can paint the still-mounted detail panel empty for a
    // frame. Do not "tidy" this order.
    //
    // The reset is a plain dispatch rather than `useGetOrderDetail`'s
    // `resetOrderDetail`, because only the slice write would have crossed
    // anyway: the poll timer and the request token live in refs, so this
    // hook's instance of `useGetOrderDetail` is not the one that is polling
    // and could not stop it. Navigating unmounts the detail container, and its
    // own unmount cleanup clears the timer and bumps the token.
    navigate(`/${slug}/${sessionId}/orders${query ? `?${query}` : ''}`)
    dispatch(clearOrderDetail())
  }

  /**
   * Re-read the order on the Detail screen. The retry for `error` and
   * `notFound`, and the only way to *see the order* from `timeout`, where the
   * panel hides the body: the header's back control is drawn in every state,
   * so leaving was never the problem — re-reading was. Before COM-779 the
   * shopper's only route to the order itself was to leave and open it again.
   *
   * A bare dispatch, for the reason `backToHistory` resets with one. Calling
   * `useGetOrderDetail().fetchOrderDetail` from here would fetch through a
   * *second* instance of that hook, which owns its own `pollTimerRef` and
   * `requestIdRef`: it could neither cancel the live poll nor be cancelled by
   * it, so a refresh would leave two chains writing `selected` on separate
   * timers. The nonce crosses to the instance that owns the timer; a ref
   * cannot be reached from here at all.
   *
   * No guard on there being an order to refresh. This hook cannot see one —
   * the route id lives on the Detail screen — and the guard belongs with the
   * instance that would act on the edge, which is where it is.
   */
  const refreshOrderDetail = () => {
    dispatch(refreshOrderDetailAction())
  }

  /**
   * Leaves the webview for the conversation the shopper arrived from.
   *
   * The exit from the confirmation screen, and required on every terminal
   * state of it — an order is placed by then, so a screen with no way out is
   * a dead end with the shopper's money already spent.
   *
   * Resolution is `getGoToChannelUrl`'s and the two fallbacks are the error
   * route's, deliberately: `sessionData.workflow.channelUid` first, the
   * storefront's configured `phoneNumber` second, WhatsApp as the channel when
   * the session names none. Reused rather than rebuilt (M1-B D-4); there were
   * already two ways to construct this URL in the repo and a third would be
   * one more thing to keep in step.
   *
   * One divergence from the route it mirrors: that one passes
   * `text: botMessage`, which seeds the conversation and restarts the bot.
   * This passes none, so the channel opens with an empty message — the
   * shopper's order is already placed and there is nothing to restart.
   *
   * A missing phone number leaves the shopper where they are rather than
   * navigating to a broken URL. The panel cannot know that happened: its
   * `data-exit="unavailable"` marker fires on `!inFlight && !showsReturn`,
   * i.e. only when the *label* is missing on a terminal state — so a
   * configured label with no phone number draws the button and publishes
   * nothing. That is exactly why the no-op is reported rather than merely
   * returned. A gap worth naming in a
   * comment is a gap worth one line of telemetry: this is the shopper's only
   * exit from a screen where their money has already moved, and without the
   * report the symptom is "the button does nothing" with no trace anywhere.
   */
  const returnToConversation = () => {
    const phoneNumber = workflow?.channelUid || configPhoneNumber
    if (!phoneNumber) {
      const context = generateDataDogContext({
        title: 'return to conversation had no channel or phone number',
        extraInfo: {
          function: 'returnToConversation',
          hasChannelUid: !!workflow?.channelUid,
          hasConfigPhoneNumber: !!configPhoneNumber,
        },
      })
      datadogRum.startView(context.viewName)
      datadogRum.addError(
        new Error('no channelUid and no configured phoneNumber'),
        context,
      )
      return
    }

    const channel = workflow?.channel || WorkflowChannel.WHATSAPP
    // Reported here rather than inside `getGoToChannelUrl`, which is also
    // called from a render body — see that util's note. This is a click, so
    // one report is one shopper hitting the fallback.
    if (!isSupportedChannel(channel)) {
      const context = generateDataDogContext({
        title: 'unsupported workflow channel, falling back to whatsapp',
        extraInfo: { function: 'returnToConversation', channel },
      })
      datadogRum.startView(context.viewName)
      datadogRum.addError(new Error('unsupported workflow channel'), context)
    }

    window.location.href = getGoToChannelUrl({ channel, phoneNumber })
  }

  return {
    returnToConversation,
    openOrderDetail,
    loadMore,
    filterOrders,
    backToHistory,
    refreshOrderDetail,
  }
}

export default useOrderActions
